import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudService, HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { FormioHTMLData } from "@main/formio/components/formio-pdf/formio-pdf-generator.component";
import { FileDetails } from "@main/file-storage";
import { FormIOData, FormIODefinition } from "@features/bb-formio";
import { FormDataResponse } from "@features/bb-formio/dto/form-data";
import { IEpisode } from "@project/models/episodes/episode";
import { MUFUserTargets } from "@main/formio/multi-user-form/dto/multi-user-form-stage.dto";
import { MultiUserFormDef } from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import {
    MultiUserFormAssociation,
    NewMultiUserFormAssociation
} from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";

@Injectable({
    providedIn: "root"
})
export class SelfRegistrationFormService extends CrudService<any> {
    public readonly url = "api/self-register-form-event";
    public readonly entityTitle = "Self Registration Form";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    async getFormDefinitionByOrgUrl(organizationUrl: string): Promise<FormIODefinition> {
        return await this.handleRequest<FormIODefinition>(
            this.http.get<FormIODefinition>(`${this.url}/form-definition/org-url/${organizationUrl}`),
            this.handlersFactory.getDefault());
    }

    async getFormDefinitionJsonByOrgUrl(organizationUrl: string): Promise<FormIODefinition> {
        return await this.handleRequest<FormIODefinition>(
            this.http.get<FormIODefinition>(`${this.url}/form-definition-json/org-url/${organizationUrl}`),
            this.handlersFactory.getDefault());
    }

    async createPdfFormData(
        organizationUrl: string,
        patientId: string,
        data: FormioHTMLData,
        responseHandlerSettings?: IHttpResponseHandlerSettings
    ): Promise<FileDetails> {
        return await this.handleRequest<FileDetails>(
            this.http.post<FileDetails>(
                `${this.url}/form-data/create-pdf/org-url/${organizationUrl}/patient/${patientId}`,
                data
            ),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    async saveFormData(
        organizationUrl: string,
        patientId: string,
        formData: FormIOData
    ): Promise<FormDataResponse> {
        return await this.handleRequest<FormDataResponse>(
            this.http.post<FormDataResponse>(
                `${this.url}/form-data/save/org-url/${organizationUrl}/patient/${patientId}`,
                formData
            ),
            this.handlersFactory.getDefault()
        );
    }

    public async getFormDataByUrl(organizationUrl: string, patientId: string){
        return await this.handleRequest<FormIOData>(
            this.http.get<FormIOData>(`${this.url}/form-data/org-url/${organizationUrl}/patient/${patientId}`),
            this.handlersFactory.getDefault());
    }

    public async getFormDataJsonByUrl(organizationUrl: string, patientId: string){
        return await this.handleRequest<string>(
            this.http.get<string>(`${this.url}/form-data/json/org-url/${organizationUrl}/patient/${patientId}`),
            this.handlersFactory.getDefault());
    }

    async getEpisodeByOrgId(organizationId: string): Promise<IEpisode> {
        return await this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/episode/org/${organizationId}`),
            this.handlersFactory.getDefault()
        );
    }

    async getEpisodeByUrl(organizationUrl: string): Promise<IEpisode> {
        return await this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/episode/org-url/${organizationUrl}`),
            this.handlersFactory.getDefault()
        );
    }

    public async getInstanceTargetsByUrl(organizationUrl: string): Promise<MUFUserTargets[]> {
        return await this.handleRequest<MUFUserTargets[]>(
            this.http.get<MUFUserTargets[]>(`${this.url}/multi-user-form-definition/instance-targets/org-url/${organizationUrl}`),
            this.handlersFactory.getDefault()
        );
    }

    public async getMufDefinitionByUrl(organizationUrl: string): Promise<MultiUserFormDef> {
        return await this.handleRequest<MultiUserFormDef>(
            this.http.get<MultiUserFormDef>(`${this.url}/multi-user-form-definition/org-url/${organizationUrl}`),
            this.handlersFactory.getDefault()
        );
    }

    public async addNewMufAssociationByUrl(organizationUrl: string, dto: NewMultiUserFormAssociation) {
        return await this.handleRequest<MultiUserFormAssociation>(
            this.http.post<MultiUserFormAssociation>(`${this.url}/multi-user-form-association/org-url/${organizationUrl}`, dto),
            this.handlersFactory.getForCreate("New Multi-User Form Association"));
    }

    public async getMufDataForRenderingByUrl(organizationUrl: string, patientId: string){
        return await this.handleRequest<MultiUserFormAssociation>(
            this.http.get<MultiUserFormAssociation>(`${this.url}/multi-user-form-association/render/org-url/${organizationUrl}/patientId/${patientId}`),
            this.handlersFactory.getDefault());
    }

    public async getMufDataByUrl(organizationUrl: string, patientId: string){
        return await this.handleRequest<MultiUserFormAssociation>(
            this.http.get<MultiUserFormAssociation>(`${this.url}/multi-user-form-association/org-url/${organizationUrl}/patientId/${patientId}`),
            this.handlersFactory.getDefault());
    }
}